import * as Popover from '@radix-ui/react-popover'
import { useState, useContext, useEffect } from 'react'
import { PopoverContainerContext } from '@/context/popover-container-context'
import { timestampMsToMonthDayTime } from '@/util/time'
import { Edited, EditedLeftEdge } from '@/assets/icons'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

/**
 * Width of the EditedLeftEdge icon
 * The sideOffset must match this value so that when the popover is positioned
 * to the right of the trigger, the icon perfectly overlays the triangle,
 * creating the illusion that the triangle changes colour when hovered.
 */
const EDITED_LEFT_EDGE_ICON_WIDTH_PX = 13

interface EditedIndicatorPopoverProps {
  timestampMs: number
  onIndicatorHoverChange?: (isHovered: boolean) => void
}

/**
 * EditedIndicatorPopover
 *
 * Renders an edited indicator triangle that displays a timestamp popover on hover/tap.
 */
export default function EditedIndicatorPopover(props: EditedIndicatorPopoverProps) {
  const { timestampMs, onIndicatorHoverChange } = props
  const containerEl = useContext(PopoverContainerContext)
  const isTouchOnlyDevice = useIsTouchOnlyDevice()

  // Local UI state
  const [hasInteracted, setHasInteracted] = useState(false)
  const [showPopover, setShowPopover] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Desktop: Debounce popover visibility to prevent flickering when cursor briefly passes over trigger
  useDebouncedEffect(() => {
    if (isTouchOnlyDevice) return // Touch handles popover directly, no sync needed

    setShowPopover(isHovered)
  }, [isHovered, isTouchOnlyDevice])

  // Desktop: Set hover state on mouse enter/leave
  const handleMouseHover = (isHovered: boolean) => (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent bubbling to parent (cell) hover handlers
    setIsHovered(isHovered)
    onIndicatorHoverChange?.(isHovered)
  }

  const formattedTimestamp = timestampMsToMonthDayTime(timestampMs)

  // Defer rendering Popover.Root until first interaction to reduce DOM overhead per cell
  useEffect(() => {
    if (showPopover && !hasInteracted) {
      setHasInteracted(true)
    }
  }, [showPopover, hasInteracted])

  // Before first interaction: render only the trigger icon (no Popover.Root)
  if (!hasInteracted) {
    return (
      <>
        {/* Larger touch target for easier tapping (for touch-only devices) */}
        {isTouchOnlyDevice && (
          <div
            className="absolute -right-2 -top-2 w-6 h-9 pointer-events-auto z-20"
            onClick={() => setShowPopover((prev) => !prev)}
            aria-label="Show edited timestamp"
          />
        )}
        <div
          {...(!isTouchOnlyDevice && {
            onMouseEnter: handleMouseHover(true),
            onMouseLeave: handleMouseHover(false),
          })}
          className="absolute flex items-center justify-center right-0 top-0 pointer-events-auto z-20"
          aria-label="Show edited timestamp"
          aria-hidden={isTouchOnlyDevice || undefined}
        >
          <Edited className="w-3.5 h-3.5" />
        </div>
      </>
    )
  }

  // After first interaction: mount full Popover.Root
  return (
    <Popover.Root
      open={showPopover}
      onOpenChange={(open) => {
        setShowPopover(open)
      }}
    >
      {/* Larger touch target for easier tapping (for touch-only devices) - trigger below stays as-is for correct popover positioning */}
      {isTouchOnlyDevice && (
        <div
          className="absolute -right-2 -top-2 w-6 h-9 pointer-events-auto z-20"
          onClick={() => setShowPopover((prev) => !prev)}
          aria-label="Show edited timestamp"
        />
      )}

      <Popover.Trigger asChild>
        <div
          {...(!isTouchOnlyDevice && {
            onMouseEnter: handleMouseHover(true),
            onMouseLeave: handleMouseHover(false),
          })}
          className="absolute flex items-center justify-center right-0 top-0 pointer-events-auto z-20"
          aria-label="Show edited timestamp"
          aria-hidden={isTouchOnlyDevice || undefined}
        >
          <Edited className="w-3.5 h-3.5" />
        </div>
      </Popover.Trigger>

      {/* Pin the portal inside the container when available */}
      <Popover.Portal container={containerEl || undefined}>
        <Popover.Content
          className="z-50 flex items-stretch outline-hidden drop-shadow-[0px_0px_16px_rgba(0,0,0,0.1)]"
          side="right"
          align="end"
          sideOffset={-EDITED_LEFT_EDGE_ICON_WIDTH_PX}
          avoidCollisions
          collisionBoundary={containerEl || undefined}
          {...(!isTouchOnlyDevice && {
            onMouseEnter: handleMouseHover(true),
            onMouseLeave: handleMouseHover(false),
          })}
        >
          <div className="flex">
            {/* EditedLeftEdge icon provides the decorative left edge and corner fold design */}
            <EditedLeftEdge className="h-7 text-brand-neutral-300" style={{ width: `${EDITED_LEFT_EDGE_ICON_WIDTH_PX}px` }} />
            <div className="bg-brand-neutral-800 text-white rounded-br rounded-tr pl-1 pr-3 h-7">
              <span className="text-xs">Edited: {formattedTimestamp}</span>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
